import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {announce} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner, Link, SplitPageLayout} from '@primer/react'
import {type FC, type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FINDINGS_ANALYTICS_EVENTS, FINDINGS_APP_NAME, FINDINGS_CATEGORY} from './analytics'
import {getFindingApplicationKey, getFindingTitle, partitionFindings} from './entity-helpers'
import {FindingsHeader} from './FindingsHeader'
import {
  FindingsError,
  FindingsLoadingState,
  FindingsNoFindingsState,
  FindingsNotFoundState,
  FindingsProcessingState,
} from './FindingsLifecycleStates'
import {FindingsList} from './FindingsList'
import {FindingsSidebar, type SidebarFixTask} from './FindingsSidebar'
import {FixFindingDialog} from './FixFindingDialog'
import type {FindingResolvedReason, Finding as FindingType} from './types'
import {useDismissibleBanner} from './use-dismissible-banner'
import {useExpandedFindings} from './use-expanded-findings'
import {focusFindingAfterStatusAction, useFindingDeepLink} from './use-finding-deep-link'
import {useFilterFindings} from './use-filter-findings'
import {useFindings} from './use-findings'
import {useFindingsMutations} from './use-findings-mutations'

interface FindingsProps {
  pullRequestBasePath: string
  variant: 'page' | 'overview'
  aliveChannel?: string | null
}

export const Findings: FC<FindingsProps> = ({variant, ...props}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const headingTag = variant === 'overview' ? 'h3' : 'h2'

  return (
    <AnalyticsProvider appName={FINDINGS_APP_NAME} category={FINDINGS_CATEGORY} metadata={{variant}}>
      <ErrorBoundary
        boundaryName="Findings"
        onError={e => setErrorMessage(e.message)}
        fallback={<FindingsError variant={variant} error={errorMessage} headingTag={headingTag} />}
      >
        <Suspense fallback={<FindingsLoadingState variant={variant} />}>
          <ConsecutiveCAPI401sProvider>
            <FindingsInner {...props} variant={variant} />
          </ConsecutiveCAPI401sProvider>
        </Suspense>
      </ErrorBoundary>
    </AnalyticsProvider>
  )
}

const FindingsInner: FC<FindingsProps> = ({pullRequestBasePath, variant, aliveChannel}) => {
  const {
    findings,
    freshness,
    modelsUrl,
    mergeReadiness,
    showFeedbackBanner,
    feedbackBannerDismissPath,
    isNotFound,
    sweAgentEnabled,
    dotcomChatEnabled,
    headSha,
    canApplySuggestion,
    maxFindingBatchSize,
  } = useFindings({
    pullRequestBasePath,
    aliveChannel,
  })

  const {
    open: openFindings,
    ignored: ignoredFindings,
    outdated: outdatedFindings,
  } = useMemo(() => partitionFindings(findings), [findings])

  const {handleDismiss, handleReopen, actionError} = useFindingsMutations(pullRequestBasePath)

  const freshnessBanner = useDismissibleBanner(freshness !== 'current')
  const actionErrorBanner = useDismissibleBanner(!!actionError)
  const feedbackBanner = useDismissibleBanner(showFeedbackBanner && !!feedbackBannerDismissPath, {
    onDismiss: async () => {
      if (feedbackBannerDismissPath) {
        try {
          await verifiedFetch(feedbackBannerDismissPath, {method: 'POST'})
        } catch {
          // fail silently
        }
      }
    },
  })

  const {displayedFindings: filteredFindings, ...filterProps} = useFilterFindings(
    openFindings,
    ignoredFindings,
    outdatedFindings,
    pullRequestBasePath,
  )
  const isOverview = variant === 'overview'
  const headingTag = isOverview ? 'h3' : 'h2'

  const [fixFindings, setFixFindings] = useState<FindingType[] | null>(null)
  const [appliedFindingVersions, setAppliedFindingVersions] = useState<ReadonlyMap<string, string>>(() => new Map())
  const {expandedFindingIdSet, handleToggleFindingExpanded, handleExpandFinding, handleRemoveExpandedFindings} =
    useExpandedFindings(pullRequestBasePath)

  const {linkedFinding, linkedView, clearLinkedFinding} = useFindingDeepLink({
    openFindings,
    ignoredFindings,
    outdatedFindings,
    onExpandFinding: handleExpandFinding,
    enabled: !isOverview,
  })
  const displayedFindings = useMemo(
    () => (linkedFinding ? [linkedFinding] : filteredFindings),
    [filteredFindings, linkedFinding],
  )
  const selectedView = linkedView ?? filterProps.selectedView
  const linkedFindingId = linkedFinding?.id ?? null
  const cancelStatusActionFocusRef = useRef<(() => void) | null>(null)
  const focusAfterFindingAction = useCallback((findingId: string) => {
    cancelStatusActionFocusRef.current?.()
    cancelStatusActionFocusRef.current = focusFindingAfterStatusAction(findingId)
  }, [])
  useEffect(
    () => () => {
      cancelStatusActionFocusRef.current?.()
      cancelStatusActionFocusRef.current = null
    },
    [linkedFindingId],
  )

  // handling optimistic updates for fix tasks
  const [fixCcaTaskIds, setFixCcaTaskIds] = useState<Record<string, string>>({})
  const handleTaskCreated = useCallback((ccaTaskId: string, _taskUrl: string, findingIds: string[]) => {
    setFixCcaTaskIds(prev => {
      const next = {...prev}
      for (const id of findingIds) next[id] = ccaTaskId
      return next
    })
  }, [])
  const handleSuggestionApplied = useCallback(
    (finding: FindingType) => {
      if (headSha) {
        setAppliedFindingVersions(previousVersions => {
          const nextVersions = new Map(previousVersions)
          nextVersions.set(finding.id, getFindingApplicationKey(finding, headSha))
          return nextVersions
        })
      }
      focusAfterFindingAction(finding.id)
    },
    [focusAfterFindingAction, headSha],
  )
  const handleDismissFindings = useCallback(
    async (findingsToDismiss: FindingType[], resolvedReason: FindingResolvedReason) => {
      const linkedFindingToDismiss = findingsToDismiss.find(finding => finding.id === linkedFindingId)
      if (linkedFindingToDismiss) {
        handleExpandFinding(linkedFindingToDismiss.id)
        focusAfterFindingAction(linkedFindingToDismiss.id)
      } else {
        requestAnimationFrame(() => {
          document.getElementById('findings-heading')?.focus({preventScroll: true})
        })
      }

      const didDismiss = await handleDismiss(findingsToDismiss, resolvedReason)
      if (didDismiss) {
        handleRemoveExpandedFindings(
          findingsToDismiss.filter(finding => finding.id !== linkedFindingId).map(finding => finding.id),
        )
      }
    },
    [focusAfterFindingAction, handleDismiss, handleExpandFinding, handleRemoveExpandedFindings, linkedFindingId],
  )
  const handleReopenFindings = useCallback(
    (findingsToReopen: FindingType[]) => {
      const linkedFindingToReopen = findingsToReopen.find(finding => finding.id === linkedFindingId)
      if (linkedFindingToReopen) {
        handleExpandFinding(linkedFindingToReopen.id)
        focusAfterFindingAction(linkedFindingToReopen.id)
      } else {
        requestAnimationFrame(() => {
          document.getElementById('findings-heading')?.focus({preventScroll: true})
        })
      }
      handleReopen(findingsToReopen)
    },
    [focusAfterFindingAction, handleExpandFinding, handleReopen, linkedFindingId],
  )

  const taskBasePath = pullRequestBasePath.replace(/\/pull\/.*$/, '')

  const sidebarFixTasks: SidebarFixTask[] = useMemo(() => {
    const tasks: SidebarFixTask[] = []
    for (const finding of displayedFindings) {
      const ccaTaskId = fixCcaTaskIds[finding.id] ?? finding.ccaTaskId
      if (!ccaTaskId) continue
      tasks.push({
        findingTitle: getFindingTitle(finding),
        ccaTaskId,
        taskBasePath,
      })
    }
    return tasks
  }, [displayedFindings, fixCcaTaskIds, taskBasePath])

  // Announce when findings finish processing (freshness transitions to 'current')
  const previousFreshnessRef = useRef(freshness)
  useEffect(() => {
    if (previousFreshnessRef.current !== 'current' && freshness === 'current') {
      announce('Findings updated')
    }
    previousFreshnessRef.current = freshness
  }, [freshness])

  // Announce when no findings are available (so screen reader users know loading finished)
  useEffect(() => {
    if (isNotFound) {
      announce('No findings available')
    }
  }, [isNotFound])

  // Emit a single impression event when the findings tab is opened (powers
  // active/returning-user metrics on the findings usage dashboard).
  const {sendAnalyticsEvent} = useAnalytics()
  const hasSentViewRef = useRef(false)
  useEffect(() => {
    if (hasSentViewRef.current) return
    hasSentViewRef.current = true
    sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.view.type, FINDINGS_ANALYTICS_EVENTS.view.target, {
      freshness,
      open_count: openFindings.length,
      ignored_count: ignoredFindings.length,
    })
  }, [sendAnalyticsEvent, freshness, openFindings.length, ignoredFindings.length])

  if (isNotFound) {
    return <FindingsNotFoundState variant={variant} headingTag={headingTag} />
  }

  const hasNoFindings = openFindings.length === 0 && ignoredFindings.length === 0 && outdatedFindings.length === 0

  // Only take over the whole view with the processing state when we have nothing
  // to show yet. If findings are already present, render them and let the
  // freshness banner convey that more are still being processed.
  if (freshness === 'pending' && hasNoFindings) {
    return <FindingsProcessingState variant={variant} headingTag={headingTag} />
  }

  if (hasNoFindings) {
    return <FindingsNoFindingsState variant={variant} headingTag={headingTag} />
  }

  return (
    <>
      <FindingsContentContainer variant={variant}>
        {!isOverview && (
          <FindingsHeader
            displayedCount={displayedFindings.length}
            handleFix={() => setFixFindings(displayedFindings)}
            sweAgentEnabled={sweAgentEnabled}
          />
        )}
        {linkedFinding && (
          <Banner
            variant="info"
            layout="compact"
            className="tmp-mb-3"
            aria-label="Showing linked finding"
            primaryAction={<Banner.PrimaryAction onClick={clearLinkedFinding}>Show all findings</Banner.PrimaryAction>}
          >
            <Banner.Title as="h3">Showing linked finding</Banner.Title>
            <Banner.Description>Filters are temporarily hidden while you view this finding.</Banner.Description>
          </Banner>
        )}
        {freshnessBanner.visible && (
          <Banner
            hideTitle
            variant="info"
            layout="compact"
            className="tmp-mb-3"
            title="Updating findings"
            onDismiss={freshnessBanner.dismiss}
          >
            New findings are being processed and will appear shortly
          </Banner>
        )}
        {actionErrorBanner.visible && (
          <div role="alert">
            <Banner
              hideTitle
              layout="compact"
              variant="warning"
              className="tmp-mb-3"
              title="Action failed"
              onDismiss={actionErrorBanner.dismiss}
            >
              {actionError}
            </Banner>
          </div>
        )}
        {feedbackBanner.visible && (
          <Banner
            hideTitle
            variant="info"
            layout="compact"
            className="tmp-mb-3"
            title="Share feedback on Copilot findings"
            onDismiss={feedbackBanner.dismiss}
          >
            <Banner.Description>
              Help us improve Copilot findings by sharing feedback in{' '}
              <Link inline href="https://github-grid.enterprise.slack.com/archives/C0B6A3D7Y5C" target="_blank">
                Slack
              </Link>
              .
            </Banner.Description>
          </Banner>
        )}
        <FindingsList
          displayedFindings={displayedFindings}
          selectedView={selectedView}
          attentionCount={filterProps.filteredAttentionCount}
          otherCount={filterProps.filteredOtherCount}
          ignoredCount={filterProps.filteredIgnoredCount}
          outdatedCount={filterProps.filteredOutdatedCount}
          expandedFindingIds={expandedFindingIdSet}
          fixCcaTaskIds={fixCcaTaskIds}
          pullRequestBasePath={pullRequestBasePath}
          onViewChange={filterProps.handleViewChange}
          onToggleExpanded={handleToggleFindingExpanded}
          onDismiss={(finding, resolvedReason) => handleDismissFindings([finding], resolvedReason)}
          onReopen={finding => handleReopenFindings([finding])}
          onFix={finding => setFixFindings([finding])}
          sweAgentEnabled={sweAgentEnabled}
          dotcomChatEnabled={dotcomChatEnabled}
          headSha={headSha}
          canApplySuggestion={canApplySuggestion && freshness === 'current'}
          appliedFindingVersions={appliedFindingVersions}
          onSuggestionApplied={handleSuggestionApplied}
        />
        <p className="text-center color-fg-muted text-small mt-3">
          <Link
            className="Link--inTextBlock"
            href="https://docs.github.com/en/copilot/responsible-use-of-github-copilot-features/responsible-use-of-github-copilot-code-review"
            inline
            muted
          >
            Copilot
          </Link>
          {' uses AI. Check for mistakes.'}
        </p>
      </FindingsContentContainer>
      {!isOverview && !linkedFinding && (
        <SplitPageLayout.Pane position="end" sticky divider="none" padding="condensed" width="large">
          <FindingsSidebar
            selectedView={filterProps.selectedView}
            attentionCount={filterProps.attentionCount}
            otherCount={filterProps.otherCount}
            ignoredCount={filterProps.ignoredCount}
            outdatedCount={filterProps.outdatedCount}
            onViewChange={filterProps.handleViewChange}
            availableSources={filterProps.availableSources}
            selectedSources={filterProps.selectedSources}
            onSourceToggle={filterProps.handleSourceToggle}
            availableTools={filterProps.availableTools}
            selectedTools={filterProps.selectedTools}
            onToolToggle={filterProps.handleToolToggle}
            fixTasks={sidebarFixTasks}
            mergeReadiness={mergeReadiness}
          />
        </SplitPageLayout.Pane>
      )}
      {fixFindings && headSha && (
        <FixFindingDialog
          findings={fixFindings}
          basePath={pullRequestBasePath}
          headSha={headSha}
          maxFindingBatchSize={maxFindingBatchSize}
          modelsUrl={modelsUrl}
          onClose={() => setFixFindings(null)}
          onTaskCreated={handleTaskCreated}
        />
      )}
    </>
  )
}

interface FindingsContentContainerProps {
  variant: FindingsProps['variant']
  children: ReactNode
}

const FindingsContentContainer: FC<FindingsContentContainerProps> = ({variant, children}) => {
  if (variant === 'overview') {
    return (
      <div id="findings-content" role="region" aria-label="Copilot findings" tabIndex={-1}>
        {children}
      </div>
    )
  }

  return (
    <SplitPageLayout.Content as="div" padding="none">
      {children}
    </SplitPageLayout.Content>
  )
}
